import { LoginOptions } from "@remo-co/types";
import { SOCIAL_AUTH_PROVIDERS } from "common";
import { useI18n } from "i18n";
import GoogleSSOButton from "modules/auth/authButtons/GoogleSSO";
import LinkedInSSOButton from "modules/auth/authButtons/LinkedInSSO";
import MicrosoftSSOButton from "modules/auth/authButtons/MicrosoftSSO";
import SAMLSSOButton from "modules/auth/authButtons/SAMLSSOButton";
import FacebookSSOButton from "modules/auth/authButtons/FacebookSSO";
import { CompanyPublicSettings } from "modules/company/types";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Divider } from "@remo-co/ui-core/src/components/Divider";
import { useSelector } from "react-redux";
import {
  selectPreferredLoginMethods,
  selectShowUserPreferredLoginBypass,
} from "modules/auth/redux/selectors";
import { IEvent } from "modules/event/types";
import { AnonymousLoginButton } from "modules/eventLanding/components";
import EDCSSOButton from "modules/auth/authButtons/EDCSSO";
import { useStyles } from "./styles";
import { nonSsoLoginOptions } from "../../constants";

interface Props {
  event: IEvent;
  eventCompanySettings: CompanyPublicSettings | undefined;
  isEventHappeningNow: boolean;
  showAnonymousLoginOption: boolean;
  onAnonymousLoginClick: () => void;
}

export const ThirdPartyOptions = ({
  event,
  eventCompanySettings,
  isEventHappeningNow,
  showAnonymousLoginOption,
  onAnonymousLoginClick,
}: Props) => {
  const { t } = useI18n(["auth", "eventLanding"]);
  const styles = useStyles();
  const showUserPreferredLoginBypass = useSelector(
    selectShowUserPreferredLoginBypass,
  );
  const preferredLoginMethods = useSelector(selectPreferredLoginMethods);

  const showLoginMethod = (method: LoginOptions) =>
    (!showUserPreferredLoginBypass &&
      eventCompanySettings?.loginOptions[method]) ||
    (showUserPreferredLoginBypass &&
      (preferredLoginMethods.includes(method) ||
        preferredLoginMethods.includes(`${method}.com`)));

  const SAMLProvider =
    eventCompanySettings?.identityProviders?.type === "saml2"
      ? eventCompanySettings.identityProviders
      : undefined;

  const areAllSsoOptionsDisabled =
    eventCompanySettings?.loginOptions &&
    Object.entries(eventCompanySettings.loginOptions)
      .filter(([option]) => {
        if (option === LoginOptions.CUSTOM_SSO) {
          return !!SAMLProvider?.isEnabled;
        }

        return !nonSsoLoginOptions.includes(option);
      })
      .every(([_, value]) => value === false);

  const showDivider =
    (!showUserPreferredLoginBypass && !areAllSsoOptionsDisabled) ||
    (showUserPreferredLoginBypass &&
      preferredLoginMethods.filter((method) => method !== "emailLink").length >
        0);

  return (
    <>
      <EDCSSOButton
        text={t("auth:login.with.provider", {
          provider: SOCIAL_AUTH_PROVIDERS.EDC,
        })}
        isEventHappeningNow={isEventHappeningNow}
      />
      {/* show provider if configured and event supports it OR we're showing preferred login methods */}
      {SAMLProvider?.isEnabled &&
        (eventCompanySettings?.loginOptions[LoginOptions.CUSTOM_SSO] ||
          showUserPreferredLoginBypass) && (
          <div>
            <SAMLSSOButton
              providerName={SAMLProvider.providerId}
              isEventHappeningNow={isEventHappeningNow}
            >
              {`Log in with ${SAMLProvider.name}`}
            </SAMLSSOButton>
          </div>
        )}
      {showLoginMethod(LoginOptions.MICROSOFT) && (
        <div>
          <MicrosoftSSOButton
            text={t("auth:login.with.provider", {
              provider: SOCIAL_AUTH_PROVIDERS.MICROSOFT,
            })}
            isEventHappeningNow={isEventHappeningNow}
          />
        </div>
      )}
      {showLoginMethod(LoginOptions.GOOGLE) && (
        <div>
          <GoogleSSOButton
            text={t("auth:login.with.provider", {
              provider: SOCIAL_AUTH_PROVIDERS.GOOGLE,
            })}
            isEventHappeningNow={isEventHappeningNow}
          />
        </div>
      )}
      {showLoginMethod(LoginOptions.LINKEDIN) && (
        <div>
          <LinkedInSSOButton
            text={t("auth:login.with.provider", {
              provider: SOCIAL_AUTH_PROVIDERS.LINKEDIN,
            })}
            isEventHappeningNow={isEventHappeningNow}
          />
        </div>
      )}
      {showLoginMethod(LoginOptions.FACEBOOK) && (
        <div>
          <FacebookSSOButton
            text={t("auth:login.with.provider", {
              provider: SOCIAL_AUTH_PROVIDERS.FACEBOOK,
            })}
            isEventHappeningNow={isEventHappeningNow}
          />
        </div>
      )}
      {showAnonymousLoginOption && !showUserPreferredLoginBypass && (
        <AnonymousLoginButton event={event} onClick={onAnonymousLoginClick} />
      )}
      {showDivider && (
        <div className={styles.divider}>
          <Divider light className={styles.dividerHr} />
          <Typography className={styles.orText} component="h5">
            {t("eventLanding:span.or")}
          </Typography>
        </div>
      )}
    </>
  );
};
